import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import GetAppIcon from '@mui/icons-material/GetApp';
import MenuIcon from '@mui/icons-material/Menu';
import PageviewIcon from '@mui/icons-material/Pageview';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useState } from 'react';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { defaultDimensions } from '@/utils/analytics';
import { getUserId } from '@/utils/auth';
import { ANALYTICS_EVENT_ACTIONS, ANALYTICS_EVENTS } from '@/utils/constants';
import LegendToggleIcon from '../Icons/LegendToggleIcon';
import { DOWNLOAD_MONTHLY_DATA, DOWNLOAD_YEARLY_DATA } from './constants';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
  },
  appbar: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'row',
    boxShadow: 'none',
    border: 'solid rgba(0, 0, 0, 0.12) thin',
    alignItems: 'center',
    paddingLeft: '6px',
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  button: {
    boxShadow: 'none',
    backgroundColor: 'white',
    borderLeft: 'solid rgba(0, 0, 0, 0.12) thin',
    borderRadius: '0',
    minWidth: '36px',
  },
  accentBackground: {
    ...theme.palette.transparentBackgroundAccent,
  },
  link: {
    '&.Mui-disabled': {
      pointerEvents: 'auto',
    },
    color: 'inherit',
    textDecoration: 'none',
    fontFamily: theme.fonts.base,
    fontSize: '14px',
    [theme.breakpoints.down('lg')]: {
      fontSize: '12px',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '10px',
    },
  },
  title: {
    boxShadow: 'none',
    backgroundColor: 'white',
    borderRight: 'solid rgba(0, 0, 0, 0.12) thin',
    borderRadius: '0',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    justifyContent: 'flex-start',
  },
  transparent: {
    backgroundColor: 'transparent',
    color: 'white',
  },
  transparentButton: {
    backgroundColor: 'transparent',
    color: 'white',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
}));

const GraphContainer = ({
  children,
  open,
  setClose,
  setOpen,
  setOpenControlsModal,
  setShowPlotlyLegend,
  showLineToggle,
  showPlotlyLegend,
  showPlotlyLegendToggle,
  stacked,
  setStacked,
  title,
  loading,
  downloadCallback,
  appBarId,
  monthly,
  consolidateAssetsProps,
  linkToPage,
  darkTheme,
  googleAnalyticsTag,
  disableDownload,
  disableStackedChart,
  enableChartSettingsButton = true,
}) => {
  const classes = useStyles();
  const [menuEl, setMenuEl] = useState(null);
  const { setSnack, flashDownloadLimitSnack } = useSnackBarContext();

  const handleMenuClick = event => {
    setMenuEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setMenuEl(null);
  };
  const handleDownload = dateType => {
    gtag('event', ANALYTICS_EVENTS.event, {
      event_category: googleAnalyticsTag,
      event_action: ANALYTICS_EVENT_ACTIONS.download,
      event_label: 'production_excel',
      userId: getUserId(),
      ...defaultDimensions,
    });
    setSnack({
      open: true,
      message: 'Your download should begin shortly.',
      severity: 'success',
    });
    downloadCallback(
      errors => setSnack({ open: true, message: errors[0].message, severity: 'error' }),
      flashDownloadLimitSnack,
      dateType,
    );
  };
  const handleMenuItemClick = dateType => {
    handleDownload(dateType);
    handleMenuClose();
  };

  return (
    <Paper
      className={clsx({ [classes.root]: true, [classes.transparent]: !!darkTheme })}
      square
      elevation={1}
    >
      <AppBar
        id={appBarId}
        className={clsx({ [classes.appbar]: true, [classes.accentBackground]: !!darkTheme })}
        style={title ? { justifyContent: 'space-between' } : { justifyContent: 'flex-end' }}
        position='static'
      >
        {title && (
          <Tooltip title={title}>
            <Typography variant='body1' noWrap>
              {title}
            </Typography>
          </Tooltip>
        )}
        <div className={classes.buttonsContainer}>
          {linkToPage && (
            <Tooltip title={linkToPage.title}>
              <span>
                <Button
                  variant='contained'
                  className={clsx({
                    [classes.button]: true,
                    [classes.transparentButton]: !!darkTheme,
                  })}
                  onClick={() => {
                    gtag('event', ANALYTICS_EVENTS.event, {
                      event_category: googleAnalyticsTag,
                      event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
                      event_label: 'view_portfolio_page',
                      userId: getUserId(),
                      ...defaultDimensions,
                    });
                    if (linkToPage.callback) {
                      linkToPage.callback();
                    }
                    window.open(linkToPage.page, '_blank');
                  }}
                >
                  <PageviewIcon />
                </Button>
              </span>
            </Tooltip>
          )}
          {showPlotlyLegendToggle && (
            <Tooltip title='Toggle Graph Legend'>
              <Button
                variant='contained'
                className={clsx({
                  [classes.button]: true,
                  [classes.transparentButton]: !!darkTheme,
                })}
                onClick={() => {
                  gtag('event', ANALYTICS_EVENTS.event, {
                    event_category: googleAnalyticsTag,
                    event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
                    event_label: 'toggle_plotly_legend',
                    userId: getUserId(),
                    ...defaultDimensions,
                  });
                  setShowPlotlyLegend(!showPlotlyLegend);
                }}
              >
                <LegendToggleIcon />
              </Button>
            </Tooltip>
          )}
          {showLineToggle && (
            <Tooltip title='Toggle Line and Stacked'>
              <Button
                variant='contained'
                className={clsx({
                  [classes.button]: true,
                  [classes.transparentButton]: !!darkTheme,
                })}
                disabled={disableStackedChart}
                onClick={() => {
                  gtag('event', ANALYTICS_EVENTS.event, {
                    event_category: googleAnalyticsTag,
                    event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
                    event_label: 'toggle_stacked_graph',
                    userId: getUserId(),
                    ...defaultDimensions,
                  });
                  setStacked(!stacked);
                }}
              >
                <ShowChartIcon />
              </Button>
            </Tooltip>
          )}
          {open ? (
            <Tooltip title='Exit Fullscreen'>
              <Button
                variant='contained'
                className={clsx({
                  [classes.button]: true,
                  [classes.transparentButton]: !!darkTheme,
                })}
                onClick={() => {
                  gtag('event', ANALYTICS_EVENTS.event, {
                    event_category: googleAnalyticsTag,
                    event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
                    event_label: 'exit_fullscreen',
                    userId: getUserId(),
                    ...defaultDimensions,
                  });
                  setClose();
                }}
              >
                <FullscreenExitIcon />
              </Button>
            </Tooltip>
          ) : (
            <Tooltip title='Fullscreen'>
              <Button
                variant='contained'
                className={clsx({
                  [classes.button]: true,
                  [classes.transparentButton]: !!darkTheme,
                })}
                onClick={() => {
                  gtag('event', ANALYTICS_EVENTS.event, {
                    event_category: googleAnalyticsTag,
                    event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
                    event_label: 'enter_fullscreen',
                    userId: getUserId(),
                    ...defaultDimensions,
                  });
                  setOpen();
                }}
              >
                <FullscreenIcon />
              </Button>
            </Tooltip>
          )}
          {!disableDownload && !consolidateAssetsProps && (
            <Tooltip title='Download Data'>
              <span>
                <Button
                  variant='contained'
                  className={clsx({
                    [classes.button]: true,
                    [classes.transparentButton]: !!darkTheme,
                  })}
                  disabled={loading}
                  onClick={
                    monthly || consolidateAssetsProps ? handleMenuClick : () => handleDownload()
                  }
                >
                  <GetAppIcon />
                </Button>
              </span>
            </Tooltip>
          )}
          {monthly && (
            <Menu
              anchorEl={menuEl}
              keepMounted
              open={Boolean(menuEl)}
              onClose={() => handleMenuClose()}
            >
              <MenuItem
                className={classes.link}
                onClick={() => handleMenuItemClick(DOWNLOAD_YEARLY_DATA)}
              >
                Yearly
              </MenuItem>
              <MenuItem
                className={classes.link}
                onClick={() => handleMenuItemClick(DOWNLOAD_MONTHLY_DATA)}
              >
                Monthly
              </MenuItem>
            </Menu>
          )}
          {consolidateAssetsProps && (
            <Tooltip title='Download Data'>
              <span>
                <Button
                  variant='contained'
                  className={clsx({
                    [classes.button]: true,
                    [classes.transparentButton]: !!darkTheme,
                  })}
                  disabled={loading}
                  onClick={handleDownload}
                >
                  <GetAppIcon />
                </Button>
              </span>
            </Tooltip>
          )}
          {enableChartSettingsButton && (
            <Tooltip title='Chart Settings'>
              <Button
                variant='contained'
                className={clsx({
                  [classes.button]: true,
                  [classes.transparentButton]: !!darkTheme,
                })}
                onClick={() => {
                  gtag('event', ANALYTICS_EVENTS.event, {
                    event_category: googleAnalyticsTag,
                    event_action: ANALYTICS_EVENT_ACTIONS.buttonClick,
                    event_label: 'chart_settings',
                    userId: getUserId(),
                    ...defaultDimensions,
                  });
                  setOpenControlsModal(true);
                }}
              >
                <MenuIcon />
              </Button>
            </Tooltip>
          )}
        </div>
      </AppBar>
      <div style={{ height: 'calc(100% - 36px)' }}>{children}</div>
    </Paper>
  );
};

export default GraphContainer;
